import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { format, lastDayOfMonth } from 'date-fns';
import { AiOutlineArrowUp } from 'react-icons/ai';
import Modal from '@/components/common/Modal';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import Spacer from '@/components/common/Spacer';
import { Theme } from '@/styles/Theme';
import { dateFormat } from '@/utils/convertDateFormat';
import useCompanyInfo from '@/hooks/useCompanyInfo';
import { postPaymentResult } from '@/apis/payment';
import { upgradePostpaidPlan } from '@/apis/company';
import type { PostPaymentResultRequestProps } from '@/types/payment.types';
import type { ErrorResponse } from '@/types/client.types';
import { QUERY_KEY } from '@/constants/queryKey';
import * as S from './style';

interface Props {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeWorkerCount: number;
  pricePerWorker?: number;
}

const UpgradePlanModal = ({
  setIsModalOpen,
  activeWorkerCount,
  pricePerWorker = 220000,
}: Props) => {
  const { data: companyData } = useCompanyInfo();
  const { companyId, companyName, email, phone, paymentMethod: userPaymentMethod, subscriptionStatus } = companyData?.data || {};

  // Determine available payment methods and default
  const getAvailablePaymentMethods = (): {
    available: ('prepaid' | 'postpaid')[];
    default: 'prepaid' | 'postpaid';
  } => {
    // If user already has a payment method set, only show that option
    if (userPaymentMethod === 'postpaid') {
      return {
        available: ['postpaid'],
        default: 'postpaid',
      };
    }
    if (userPaymentMethod === 'prepaid') {
      return {
        available: ['prepaid'],
        default: 'prepaid',
      };
    }
    // Free plan: only show postpaid option (default)
    return {
      available: ['postpaid'],
      default: 'postpaid',
    };
  };

  const { available, default: defaultMethod } = getAvailablePaymentMethods();
  const [paymentMethod, setPaymentMethod] = useState<'prepaid' | 'postpaid'>(defaultMethod);

  // Update payment method if userPaymentMethod changes
  useEffect(() => {
    if (userPaymentMethod) {
      setPaymentMethod(userPaymentMethod);
    }
  }, [userPaymentMethod]);

  const queryClient = useQueryClient();

  // Calculate prorated price and breakdown info
  const { proratedPrice, fullMonthPrice, prorationInfo } = useMemo(() => {
    const fullPrice = activeWorkerCount * pricePerWorker;
    
    if (paymentMethod === 'prepaid' || paymentMethod === 'postpaid') {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day
      const lastDay = lastDayOfMonth(today);
      lastDay.setHours(0, 0, 0, 0);
      
      const oneDay = 24 * 60 * 60 * 1000;
      const totalDays = Math.round((lastDay.getTime() - today.getTime()) / oneDay) + 1;
      const monthDays = lastDay.getDate();
      let ratio = totalDays / monthDays;
      if (ratio > 1.0) {
        ratio = 1.0;
      }
      
      const prorated = Math.round(activeWorkerCount * pricePerWorker * ratio);
      
      // Format dates for display
      const startDate = format(today, 'yyyy-MM-dd');
      const endDate = format(lastDay, 'yyyy-MM-dd');
      
      return {
        proratedPrice: prorated,
        fullMonthPrice: fullPrice,
        prorationInfo: {
          totalDays,
          monthDays,
          ratio,
          showProration: true,
          startDate,
          endDate,
        },
      };
    }
    
    return {
      proratedPrice: fullPrice,
      fullMonthPrice: fullPrice,
      prorationInfo: {
        totalDays: 0,
        monthDays: 0,
        ratio: 1.0,
        showProration: false,
      },
    };
  }, [paymentMethod, activeWorkerCount, pricePerWorker]);

  const totalPrice = proratedPrice;
  const pay_oid = `upgrade_plan_${companyId}_${new Date().getTime()}`;

  const { mutate: paymentMutate, isLoading: isRegistering } = useMutation({
    mutationFn: postPaymentResult,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.CARD_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
      toast.success('플랜이 업그레이드되었습니다.');
      setIsModalOpen(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || '플랜 업그레이드에 실패했습니다.');
    },
  });

  const { mutate: postpaidUpgradeMutate, isLoading: isPostpaidUpgrading } = useMutation({
    mutationFn: upgradePostpaidPlan,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);
      toast.success('플랜이 업그레이드되었습니다.');
      setIsModalOpen(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || '플랜 업그레이드에 실패했습니다.');
    },
  });

  const callback = (response: any) => {
    const { PCD_PAY_MSG, PCD_PAY_RST } = response;
    const date = format(new Date(), 'yyyy-MM-dd');

    alert(PCD_PAY_MSG);

    if (PCD_PAY_RST === 'success') {
      const body: PostPaymentResultRequestProps = {
        callbackData: response,
        data: {
          new_card: 1,
          total_price: totalPrice,
          date: date,
          pay_oid: pay_oid,
          worker_list: {
            add: [],
            restore: [],
          },
        },
      };

      paymentMutate(body);
    }
  };

  const handleUpgrade = () => {
    if (paymentMethod === 'prepaid') {
      // Prepaid: Register card + Payment via Payple
      if (!window.PaypleCpayAuthCheck) {
        toast.error('결제 시스템을 사용할 수 없습니다.');
        return;
      }

      const data = {
        clientKey: process.env.REACT_APP_PAYPLE_CLIENTKEY,
        PCD_PAY_TYPE: 'card',
        PCD_PAY_WORK: 'PAY',
        PCD_CARD_VER: '01',
        PCD_PAY_GOODS: '블루멘토 플랜 업그레이드',
        PCD_PAY_TOTAL: totalPrice,
        PCD_PAY_OID: pay_oid,
        PCD_PAYER_NO: companyId,
        PCD_PAYER_NAME: companyName,
        PCD_PAYER_HP: phone,
        PCD_PAYER_EMAIL: email,
        PCD_RST_URL: '/example',
        callbackFunction: callback,
      };

      window.PaypleCpayAuthCheck(data);
    } else {
      // Postpaid: Upgrade directly without payment gateway
      postpaidUpgradeMutate();
    }
  };

  return (
    <Modal setIsModalOpen={setIsModalOpen} hasButton={false}>
      <S.Container>
        <S.Header>
          <S.IconWrapper>
            <AiOutlineArrowUp size={24} color={Theme.colors.WHITE} />
          </S.IconWrapper>
          <Text fontSize='1.5rem' fontWeight={700}>
            플랜 업그레이드
          </Text>
          <Text fontSize='0.875rem' color={Theme.colors.G_2}>
            무제한 작업자 및 점검지 사용, 워터마크 없는 내보내기
          </Text>
        </S.Header>

        <S.ContentWrapper>
          <S.LeftSection>
            <S.FormSection>
              <Text fontSize='0.875rem' fontWeight={600} margin='0 0 0.5rem 0'>
                결제 방법
              </Text>
              <S.RadioContainer>
                {available.includes('prepaid') && (
                  <>
                    <S.RadioOption
                      onClick={() => setPaymentMethod('prepaid')}
                      selected={paymentMethod === 'prepaid'}
                    >
                      <S.RadioInput
                        type='radio'
                        checked={paymentMethod === 'prepaid'}
                        onChange={() => setPaymentMethod('prepaid')}
                      />
                      <Text>선불 (카드)</Text>
                    </S.RadioOption>
                    {subscriptionStatus === 'free' && paymentMethod === 'prepaid' && (
                      <S.PrepaidPostpaidNotice>
                        <Text fontSize='0.875rem' color={Theme.colors.DG_1}>
                          후불 결제 방식을 원하시나요?
                        </Text>
                        <Text fontSize='0.875rem' color={Theme.colors.G_1}>
                          고객센터(1670-1204)로 문의해주세요.
                        </Text>
                        <Text fontSize='0.75rem' color={Theme.colors.G_2}>
                          평일 09:00 ~ 18:00 (주말&공휴일 제외)
                        </Text>
                      </S.PrepaidPostpaidNotice>
                    )}
                  </>
                )}
                {available.includes('postpaid') && (
                  <S.RadioOption
                    onClick={() => setPaymentMethod('postpaid')}
                    selected={paymentMethod === 'postpaid'}
                  >
                    <S.RadioInput
                      type='radio'
                      checked={paymentMethod === 'postpaid'}
                      onChange={() => setPaymentMethod('postpaid')}
                    />
                    <Text>후불 (은행)</Text>
                  </S.RadioOption>
                )}
              </S.RadioContainer>
              {paymentMethod === 'postpaid' && userPaymentMethod !== 'postpaid' && (
                <S.PostpaidNotice>
                  <Text fontSize='0.875rem' color={Theme.colors.G_1}>
                    후불 결제는 영업팀과 상담이 필요합니다
                  </Text>
                </S.PostpaidNotice>
              )}
            </S.FormSection>
          </S.LeftSection>

          <S.RightSection>
            <S.BillingSection>
              <Text fontSize='1rem' fontWeight={600} margin='0 0 1rem 0'>
                결제 요약
              </Text>
              <S.TotalPrice>
                <Text fontSize='1.5rem' fontWeight={700} color={Theme.colors.B_3}>
                  {totalPrice.toLocaleString('ko-KR')}원
                </Text>
              </S.TotalPrice>
              <S.PriceBreakdown>
                <S.BreakdownLine>
                  <Text fontSize='0.875rem'>{activeWorkerCount}명 작업자</Text>
                  <Text fontSize='0.875rem'>
                    × {pricePerWorker.toLocaleString('ko-KR')}원 / month / worker
                  </Text>
                </S.BreakdownLine>
                {prorationInfo.showProration && (
                  <>
                    <S.BreakdownLine>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        월간 요금
                      </Text>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        {fullMonthPrice.toLocaleString('ko-KR')}원
                      </Text>
                    </S.BreakdownLine>
                    <S.BreakdownLineWithSpacing>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        구독 기간
                      </Text>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        {prorationInfo.startDate && prorationInfo.endDate
                          ? `${dateFormat(prorationInfo.startDate)} ~ ${dateFormat(prorationInfo.endDate)}`
                          : ''}
                      </Text>
                    </S.BreakdownLineWithSpacing>
                    <S.BreakdownLine>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        비례 계산 ({prorationInfo.totalDays}일 / {prorationInfo.monthDays}일)
                      </Text>
                      <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                        {(prorationInfo.ratio * 100).toFixed(1)}%
                      </Text>
                    </S.BreakdownLine>
                  </>
                )}
                <S.BreakdownLine>
                  <Text fontSize='0.875rem' fontWeight={600}>합계</Text>
                  <Text fontSize='0.875rem' fontWeight={600}>
                    {totalPrice.toLocaleString('ko-KR')}원
                  </Text>
                </S.BreakdownLine>
              </S.PriceBreakdown>
              <Spacer y='1rem' />
              <Text fontSize='0.75rem' color={Theme.colors.G_2}>
                월간 자동 갱신됩니다.
              </Text>
            </S.BillingSection>
          </S.RightSection>
        </S.ContentWrapper>

        <S.ButtonContainer>
          <Button
            backgroundColor={Theme.colors.G_5}
            color={Theme.colors.BLACK}
            width='12.5rem'
            height='3.125rem'
            onClick={() => setIsModalOpen(false)}
            disabled={isRegistering || isPostpaidUpgrading}
          >
            취소
          </Button>
          <Button
            backgroundColor={Theme.colors.B_3}
            color={Theme.colors.WHITE}
            width='12.5rem'
            height='3.125rem'
            onClick={handleUpgrade}
            disabled={isRegistering || isPostpaidUpgrading}
          >
            {isRegistering || isPostpaidUpgrading ? '처리 중...' : '플랜 업그레이드'}
          </Button>
        </S.ButtonContainer>
      </S.Container>
    </Modal>
  );
};

export default UpgradePlanModal;

